/**
 * quillHtmlChange
 * ----
 * Gate for controlled Quill → react-hook-form writes.
 *
 * Quill often re-emits HTML that is string-unequal but visually empty
 * (`''` ↔ `<p><br></p>` ↔ `<p></p>`). Committing every emit into RHF
 * re-feeds the controlled `value` prop and can nest setState until
 * React throws "Maximum update depth exceeded" (Sentry FLARE-7D on
 * Evaluation Desk SimpleRTE fields).
 */

/** True when HTML has no visible text after stripping tags/entities. */
export function isQuillVisuallyEmpty(html: string | null | undefined): boolean {
  return (html || '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .trim()
    .length === 0;
}

/**
 * Whether a Quill onChange should call RHF onChange.
 *
 * @param nextHtml - HTML from Quill
 * @param currentHtml - Current RHF field value
 * @param source - Quill change source (`user` | `api` | `silent`)
 */
export function shouldCommitQuillHtml(
  nextHtml: string,
  currentHtml: string | null | undefined,
  source?: string,
): boolean {
  if (source !== 'user') return false;
  const next = nextHtml ?? '';
  const current = currentHtml ?? '';
  if (next === current) return false;
  if (isQuillVisuallyEmpty(next) && isQuillVisuallyEmpty(current)) return false;
  return true;
}
